import { useEffect, useState, useCallback, useRef } from "react";
import {
  SearchOutlined,
  PeopleAltOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  CloseOutlined,
  PeopleAlt,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { getAdminSubscribers } from "../../../../services/Dashboard/Table";
import { formatDateTime } from "../../../../Utils/formatDateTime";

/**
 * Returns { label, colorClass, borderClass, bgClass, rawColor }
 *  - expired         → red
 *  - ≤ 3 days left   → orange
 *  - otherwise       → green
 */
function getTimeRemaining(sub) {
  if (sub.isExpired || sub.status === "expired") {
    return {
      label: "EXPIRED",
      colorClass: "text-[var(--color-accent-red)]",
      borderClass: "border-[rgba(255,69,0,0.25)]",
      bgClass: "bg-[rgba(255,69,0,0.12)]",
    };
  }

  const endRaw = sub.endDateRaw;
  if (!endRaw) {
    return {
      label: "EXPIRED",
      colorClass: "text-[var(--color-accent-red)]",
      borderClass: "border-[rgba(255,69,0,0.25)]",
      bgClass: "bg-[rgba(255,69,0,0.12)]",
    };
  }

  const diffMs = new Date(endRaw) - Date.now();
  if (diffMs <= 0) {
    return {
      label: "EXPIRED",
      colorClass: "text-[var(--color-accent-red)]",
      borderClass: "border-[rgba(255,69,0,0.25)]",
      bgClass: "bg-[rgba(255,69,0,0.12)]",
    };
  }

  const diffSecs = Math.floor((diffMs % 60_000) / 1000);
  const diffMins  = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays  = Math.floor(diffMs / 86_400_000);

  const isUrgent = diffDays < 3 || (diffDays === 3 && diffMs < 3 * 86_400_000);
  
  let label;
  if (diffMins === 0 && diffSecs < 60) {
    label = `${diffSecs}s left`;
  } else if (diffMins < 60) {
    label = `${diffMins}m ${diffSecs}s left`;
  } else if (diffHours < 24) {
    label = `${diffHours} hr${diffHours !== 1 ? "s" : ""} left`;
  } else {
    label = `${diffDays} day${diffDays !== 1 ? "s" : ""} left`;
  }

  if (isUrgent) {
    return {
      label,
      colorClass: "text-[var(--color-accent-amber)]",
      borderClass: "border-[rgba(255,168,0,0.25)]",
      bgClass: "bg-[rgba(255,168,0,0.12)]",
    };
  }

  return {
    label,
    colorClass: "text-[var(--color-primary)]",
    borderClass: "border-[rgba(200,255,0,0.25)]",
    bgClass: "bg-[rgba(200,255,0,0.12)]",
  };
}

function getInitial(name = "") {
  return (name.trim()[0] || "U").toUpperCase();
}

function StatCard({ icon: Icon, label, value, sub, bgIcon: BgIcon, colorClass, borderClass, bgClass }) {
  return (
    <div className={`relative overflow-hidden flex flex-col justify-between gap-4 p-4 border rounded-[var(--radius-md)] md:min-h-[150px] min-h-[140px] ${borderClass} ${bgClass}`}>
      {BgIcon && (
        <div className={`absolute -right-3 -bottom-3 opacity-[0.1] pointer-events-none ${colorClass}`}>
          <BgIcon sx={{ fontSize: 120 }} />
        </div>
      )}
      <div className={`flex-shrink-0 w-10 h-10 rounded-[var(--radius-md)] inline-flex items-center justify-center ${bgClass} ${colorClass}`}>
        <Icon sx={{ fontSize: 20 }} />
      </div>
      <div className="flex flex-col gap-1">
        <p className="md:text-sm text-xs label-caps block m-0">
          {label}
        </p>
        <h4 className="m-0 xl:text-3xl text-2xl text-[var(--color-text)] tabular font-bold leading-none">
          {value ?? "0"}
        </h4>
        {sub && (
          <p className="m-0 text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs leading-[1.5]">
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}

function SubscriberCard({ sub, onClick }) {
  const { label, colorClass, borderClass, bgClass } = getTimeRemaining(sub);
  const initial = getInitial(sub.name);

  return (
    <button
      type="button"
      onClick={() => onClick(sub)}
      className="w-full text-left flex md:flex-row flex-col items-center justify-between gap-4 min-h-[74px] p-4 first:border-t-0 border-t border-[var(--color-border)] cursor-pointer"
    >
      <div className="flex items-center gap-4 min-w-0 flex-1 w-full">
        <div className={`flex-shrink-0 xl:w-14 xl:h-14 w-12 h-12 rounded-[var(--radius-md)] border inline-flex items-center justify-center ${borderClass}`}>
          <span className={`xl:text-xl text-lg font-bold leading-none ${colorClass}`}>
            {initial}
          </span>
        </div>
        <div className="min-w-0 flex flex-col gap-1">
          <h5 className="m-0 text-[var(--color-text)] xl:text-lg md:text-base text-sm font-bold leading-none">
            {sub.name}
          </h5>
          <p className="m-0 text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs leading-[1.5]">
            {sub.email}
          </p>
        </div>
      </div>
      <div className="flex md:flex-col flex-row justify-between md:items-end items-center md:w-auto w-full gap-1">
        <span className={`inline-flex items-center px-2 py-1 rounded-[var(--radius-md)] border xl:text-xs text-[10px] font-bold uppercase tracking-[0.06em] whitespace-nowrap ${colorClass} ${borderClass} ${bgClass}`}>
          {sub.plan}
        </span>
        <span className={`m-0 xl:text-base md:text-sm text-xs leading-[1.5] font-bold md:pr-2 ${colorClass}`}>
          {label}
        </span>
      </div>
    </button>
  );
}

function SubscriptionDetailModal({ sub, onClose }) {
  const labelClass = "md:text-sm text-xs label-caps block m-0";
  const valueClass = "m-0 text-[var(--color-text)] xl:text-lg md:text-base text-sm font-bold";

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && sub && onClose) {
        onClose();
      }
    };

    if (sub) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [sub, onClose]);

  if (!sub) return null;

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 backdrop-blur-[16px]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="m-0 text-[var(--color-text)] xl:text-2xl md:text-xl text-lg font-bold">
            Subscription <span className="text-[var(--color-primary)]">Details</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] border-none cursor-pointer"
          >
            <CloseOutlined sx={{ fontSize: 20 }} />
          </button>
        </div>
        <div className="grid gap-4 p-4">
          <div className="grid gap-1">
            <p className={labelClass}>
              USER EMAIL
            </p>
            <p className={valueClass}>
              {sub.email}
            </p>
          </div>
          <div className="grid gap-1">
            <p className={labelClass}>
              FULL NAME
            </p>
            <p className={valueClass}>
              {sub.name}
            </p>
          </div>
          <div className="grid gap-1">
            <p className={labelClass}>
              CURRENT PLAN
            </p>
            <p className={valueClass}>
              {sub.plan}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1">
              <p className={labelClass}>
                START DATE
              </p>
              <p className={valueClass}>
                {formatDateTime(sub.startDate)}
              </p>
            </div>
            <div className="grid gap-1">
              <p className={labelClass}>
                END DATE
              </p>
              <p className={valueClass}>
                {formatDateTime(sub.endDate)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1">
              <p className={labelClass}>
                AUTO RENEW
              </p>
              <p className={valueClass}>
                <span className={sub.will_renew && sub.auto_debit_enabled ? "text-[var(--color-primary)]" : "text-[var(--color-accent-red)]"}>
                  {sub.will_renew && sub.auto_debit_enabled ? "Active" : "Disabled"}
                </span>
              </p>
            </div>
            {sub.cancellation_reason && (
              <div className="grid gap-1">
                <p className={labelClass}>
                  STATUS NOTE
                </p>
                <p className={valueClass}>
                  <span className="text-[var(--color-accent-amber)] capitalize">
                    {sub.cancellation_reason === "plan_deactivated" ? "Plan Deactivated" : sub.cancellation_reason.replace("_", " ")}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SubscriptionsModule() {
  const [data, setData]               = useState(null);
  const [searchTerm, setSearchTerm]   = useState("");
  const [loading, setLoading]         = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedSub, setSelectedSub] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const fetchSubscribers = useCallback(async (search = "", pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    setErrorMessage("");
    try {
      const result = await getAdminSubscribers(search, pageNum, 15);
      
      setData((prev) => {
        const newSubscribers = append ? [...(prev?.subscribers || []), ...(result.subscribers || [])] : (result.subscribers || []);
        
        setHasMore(newSubscribers.length < (result.count || 0));
        
        return {
          ...result,
          subscribers: newSubscribers
        };
      });
      setPage(pageNum);
      
    } catch (error) {
      setErrorMessage("Failed to load subscriptions details from the server.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Auto-search: fire 400 ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSubscribers(searchTerm.trim(), 1, false);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchSubscribers]);

  // Infinite scroll: auto-load more when sentinel becomes visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchSubscribers(searchTerm.trim(), page + 1, true);
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, page, fetchSubscribers, searchTerm]);

  const subscribers = data?.subscribers ?? [];

  // Calculate dynamic rates for subtexts
  const activeRateStr = data?.total_subscribers
    ? `${((data.active_subscribers / data.total_subscribers) * 100).toFixed(1)}% active rate`
    : "0.0% active rate";

  const expiredRateStr = data?.total_subscribers
    ? `${((data.expired_subscribers / data.total_subscribers) * 100).toFixed(1)}% expired rate`
    : "0.0% expired rate";

  return (
    <>
      <div className="grid gap-4">
        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
          <StatCard
            icon={PeopleAltOutlined}
            label="TOTAL SUBSCRIBERS"
            value={data?.total_subscribers}
            sub="Active & past subscriptions"
            bgIcon={PeopleAlt}
            colorClass="text-[#00A3FF]"
            borderClass="border-[rgba(0,163,255,0.25)]"
            bgClass="bg-[rgba(0,163,255,0.12)]"
          />
          <StatCard
            icon={CheckCircleOutlined}
            label="ACTIVE SUBSCRIBERS"
            value={data?.active_subscribers}
            sub={activeRateStr}
            bgIcon={CheckCircle}
            colorClass="text-[var(--color-primary)]"
            borderClass="border-[rgba(200,255,0,0.25)]"
            bgClass="bg-[rgba(200,255,0,0.12)]"
          />
          <StatCard
            icon={CancelOutlined}
            label="EXPIRED SUBSCRIBERS"
            value={data?.expired_subscribers}
            sub={expiredRateStr}
            bgIcon={Cancel}
            colorClass="text-[var(--color-accent-red)]"
            borderClass="border-[rgba(255,69,0,0.25)]"
            bgClass="bg-[rgba(255,69,0,0.12)]"
          />
        </div>

        <div className="flex justify-center">
          <span className="inline-flex items-center px-6 py-2 rounded-full border border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)] md:text-xs text-[10px] font-black tracking-[0.06em] uppercase">
            SUBSCRIPTIONS HISTORY
          </span>
        </div>

        <div className="relative flex-[1_1_260px]">
          <SearchOutlined sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 18, color: "var(--color-text-muted)" }} />
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by name, email, plan, or code..."
            className="w-full py-3 pr-4 pl-10 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none" />
        </div>

        {errorMessage && (
          <div className="px-4 py-3.5 border border-[rgba(255,69,0,0.25)] rounded-[var(--radius-md)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)] md:text-sm text-xs">
            {errorMessage}
          </div>
        )}

        <div className="border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] overflow-hidden">
          {loading ? (
            <div className="grid gap-px">
              {[1, 2, 3].map((item) => (
                <div key={item} className="skeleton h-[70px] bg-[rgba(255,255,255,0.04)]" />
              ))}
            </div>
          ) : subscribers.length === 0 ? (
            <div className="min-h-[180px] grid place-items-center xl:text-xl md:text-lg text-base text-[var(--color-text-muted)] text-center">
              No subscribers found.
            </div>
          ) : (
            <div>
              {subscribers.map((sub) => (
                <SubscriberCard key={sub.id} sub={sub} onClick={setSelectedSub} />
              ))}

              {/* Sentinel for infinite scroll + loading indicator */}
              {hasMore && (
                <div ref={sentinelRef} className="p-4 flex items-center justify-center gap-2 border-t border-[var(--color-border)]">
                  {loadingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                      <span className="md:text-sm text-xs font-bold text-[var(--color-text)]">Loading more...</span>
                    </>
                  ) : (
                    <span className="md:text-sm text-xs font-bold text-[var(--color-text)]">Scroll for more</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SubscriptionDetailModal
        sub={selectedSub}
        onClose={() => setSelectedSub(null)}
      />
    </>
  );
}

export default SubscriptionsModule;
